import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  DataTable,
  HealthDot,
  Input,
  PageHead,
  Panel,
  Pill,
  SelectX,
  StatusPill,
  Tabs,
  type Column,
} from "@guestpilot/ui-kit";
import { useGetPages, type Schema } from "../api";
import { formatDate, formatMoney } from "../format";
import { STATUS_LABEL } from "./Dashboard";

type Customer = Schema<"Customer">;

const TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "trial", label: "Trial" },
  { key: "past_due", label: "Past due" },
  { key: "churned", label: "Churned" },
  { key: "pending", label: "Pending" },
];
const PLANS = [
  { value: "", label: "All plans" },
  { value: "starter", label: "Starter" },
  { value: "business", label: "Business" },
  { value: "scale", label: "Scale" },
  { value: "enterprise", label: "Enterprise" },
];
const HEALTH = { green: "Healthy", amber: "Needs attention", red: "At risk" } as const;

/** Every customer organisation (account-level data only, BR-243). Pending enrollments have no organisation yet (BR-265). */
export function Customers() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const plan = params.get("plan") ?? "";
  const status = params.get("status") ?? "all";
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const list = useGetPages<Customer>("/customers", {
    ...(q ? { q } : {}),
    ...(plan ? { plan } : {}),
    ...(status !== "all" ? { status } : {}),
  });
  const columns: Column<Customer>[] = [
    {
      key: "name",
      header: "Customer",
      render: (c) => (
        <div className="gp-person-text">
          {c.organizationId ? (
            <Link to={`/customers/${c.organizationId}`} className="t-body-strong">
              {c.name}
            </Link>
          ) : (
            <span className="t-body-strong">{c.name}</span>
          )}
          <span className="gp-person-sub t-body-sm">{c.primaryDomain}</span>
        </div>
      ),
    },
    {
      key: "plan",
      header: "Plan",
      render: (c) =>
        c.planKey ? <Pill>{PLANS.find((p) => p.value === c.planKey)?.label ?? c.planKey}</Pill> : "—",
    },
    {
      key: "status",
      header: "Status",
      render: (c) => (
        <span className="gp-row">
          <StatusPill
            domain="customer"
            value={c.status ?? "pending"}
            label={STATUS_LABEL[c.status ?? "pending"] ?? ""}
          />
          {c.internal ? <Pill>Internal</Pill> : null}
        </span>
      ),
    },
    {
      key: "health",
      header: "Health",
      render: (c) => (c.health ? <HealthDot health={c.health} label={HEALTH[c.health]} /> : "—"),
    },
    { key: "teams", header: "Teams used", numeric: true, render: (c) => c.teamsUsed ?? 0 },
    { key: "mrr", header: "MRR", numeric: true, render: (c) => formatMoney(c.mrrCents ?? 0) },
    { key: "last", header: "Last active", render: (c) => formatDate(c.lastActiveAt) },
  ];
  return (
    <div className="gp-page">
      <PageHead title="Customers" subtitle="Every organisation using GuestPilot." />
      <div className="gp-row">
        <Input
          type="search"
          aria-label="Search by name or domain"
          placeholder="Search by name or domain"
          value={q}
          onChange={(e) => set("q", e.target.value)}
        />
        <SelectX
          aria-label="Plan"
          value={plan}
          options={PLANS}
          onChange={(e) => set("plan", e.target.value)}
        />
      </div>
      <Tabs label="Customer status" tabs={TABS} value={status} onChange={(k) => set("status", k)} />
      <Panel label="Customers">
        <DataTable
          caption="Customers"
          columns={columns}
          rows={list.items}
          loading={list.isLoading}
          error={list.isError}
          onRetry={() => void list.refetch()}
          errorTitle="Could not load the customers."
          retryLabel="Retry"
          rowKey={(c) => c.organizationId ?? c.attemptId ?? ""}
          emptyTitle="No customers match these filters"
        />
        {list.hasNextPage ? (
          <div className="gp-actions">
            <Button onClick={() => void list.fetchNextPage()}>Load more</Button>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
